/**
 * VoidVOModal Component
 * Confirms voiding a variation order. Warns if VO has payments — voiding
 * auto-creates a `refund` deduction equal to the VO's paid amount.
 */

import React, { useState } from 'react';
import { formatCurrency } from '../../utils/formatters';
import { variationOrderAPI } from '../../services/api';
import { toast } from 'react-toastify';

const VoidVOModal = ({ isOpen, onClose, onSuccess, vo, projectId }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !vo) return null;

  const paidAmount = parseFloat(vo.actualPaidAmount || 0);
  const hasPayments = paidAmount > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error('Please provide a reason for voiding this VO');
      return;
    }

    setSubmitting(true);
    try {
      const res = await variationOrderAPI.voidVO(projectId, vo.id, reason);
      toast.success(res.data?.message || 'VO voided successfully');
      onSuccess();
    } catch (err) {
      console.error('Error voiding VO:', err);
      toast.error(err.response?.data?.error || 'Failed to void VO');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-75 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Void Variation Order
            </h3>
            <p className="text-xs text-gray-500 mt-1">{vo.voNumber} · {formatCurrency(vo.amount)}</p>
          </div>

          <div className="px-4 sm:px-6 py-4 overflow-y-auto space-y-4">
            {hasPayments && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm font-medium text-red-800">
                  This VO has {formatCurrency(paidAmount)} in recorded payments.
                </p>
                <p className="text-xs text-red-700 mt-1">
                  Voiding will automatically create a <strong>refund deduction</strong> of {formatCurrency(paidAmount)} on
                  this project to keep the books balanced.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for voiding <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="e.g. duplicated with VO-003, client withdrew request…"
              />
            </div>

            <p className="text-xs text-gray-500">
              Voided VOs remain visible for history but are excluded from totals and blocked from further edits.
            </p>
          </div>

          <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-2 sm:justify-end flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
            >
              {submitting ? 'Voiding…' : 'Void VO'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VoidVOModal;
